/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { HourlyForecastChartTypes, HourlyForecastPeriodCompact, ILocationSolarDateTime, ChartHourlyForecastPeriodCompact } from '../../common/types';
import { hourlyForecastCardBaseId, isAdBreak, largeHourlyForecastViewId } from '../../common/utils';
import { reduxConnect } from '../../../../common';
import { getSolarPeriod, isSunriseSunsetHour } from '../../common/solarDivide';
import { ForecastChartView } from './forecastChartView';
import { getQueryParameterByName } from '../../../../common/utilities/queryParameters';

type ForecastChartData = {
    chartData: ChartHourlyForecastPeriodCompact[];
    chartWidth: number;
}

interface HourlyForecastChartsProps {
    hourlyForecastPeriods?: HourlyForecastPeriodCompact[];
    forecastChartName?: HourlyForecastChartTypes;
    locationSolarDateTime?: ILocationSolarDateTime[];
    chartWidth: number;
    SlugName: string;
    lastHourCardIndex: number;
    selectedHourlyForecastChart: keyof typeof HourlyForecastChartTypes;
    tempUnit: number;
}

export const getMinForecast = (data: HourlyForecastPeriodCompact[], chartName: string) => {
    if (chartName === HourlyForecastChartTypes.temp) {
        return data.reduce((prevMinTemp: number, forecastPeriod: HourlyForecastPeriodCompact) => {
            const minT = parseFloat(forecastPeriod?.t as string);
            return minT < prevMinTemp ? minT : prevMinTemp;
        }, parseFloat(data?.[0]?.t as string));
    }
    return data.reduce((prevMinPrecip, forecastPeriod: HourlyForecastPeriodCompact) => {
        return forecastPeriod?.app < prevMinPrecip ? forecastPeriod?.app : prevMinPrecip;
    }, data?.[0]?.app);
};

export const getMaxForecast = (data: HourlyForecastPeriodCompact[], chartName: string) => {
    if (chartName === HourlyForecastChartTypes.temp) {
        return data.reduce((prevMaxTemp: number, forecastPeriod: HourlyForecastPeriodCompact) => {
            const currMaxTemp = parseFloat(forecastPeriod?.t as string);
            return currMaxTemp > prevMaxTemp ? currMaxTemp : prevMaxTemp; 
        }, parseFloat(data?.[0]?.t as string));
    }
    return data.reduce((prevMaxPrecip, forecastPeriod: HourlyForecastPeriodCompact) => {
        return forecastPeriod?.app > prevMaxPrecip ? forecastPeriod?.app : prevMaxPrecip;
    }, data?.[0]?.app);
};

export const isSolarBreak = (locationSolarDateTime: ILocationSolarDateTime[] = [], forecastDateLocalStr: string) => {
    if (locationSolarDateTime?.length && forecastDateLocalStr) {
        const solarPeriod = getSolarPeriod(forecastDateLocalStr, locationSolarDateTime);
        const isSunriseHour = isSunriseSunsetHour(solarPeriod?.SunriseDateTimeLocal as string, forecastDateLocalStr);
        const isSunsetHour = isSunriseSunsetHour(solarPeriod?.SunsetDateTimeLocal as string, forecastDateLocalStr);
        if (isSunriseHour || isSunsetHour) {
            return true;
        }
    }
    return false;
};

const mapStoreStateToProps = (storeState: any) => {
    const { hourlyForecastPage, solar, appState, units } =  storeState || {};
    const { hourlyForecastPeriods, lastHourCardIndex, selectedHourlyForecastChart } = hourlyForecastPage || {};
    const { SlugName } = appState?.appState?.locality || {};
    const { tempUnit } = units || {};

    return {
        hourlyForecastPeriods,
        locationSolarDateTime: solar?.solarData,
        SlugName,
        lastHourCardIndex,
        selectedHourlyForecastChart,
        tempUnit
    };
};

const mapDispatchToProps = {};

export const HourlyForecastChart = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(memo(({ 
    hourlyForecastPeriods = [], 
    locationSolarDateTime = [],
    tempUnit=1,
    SlugName,
    lastHourCardIndex,
    selectedHourlyForecastChart = HourlyForecastChartTypes.precip }: HourlyForecastChartsProps) => {
    const initChartWidth = 2040;
    const [forecastChartData, setForecastChartData] = useState<ForecastChartData>({
        chartData: [],
        chartWidth: initChartWidth
    });

    const isLoadingFrom10DayPageRef = useRef<boolean>(false);
    const dataLoadTimeout = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const hasHourParam = getQueryParameterByName('hour');
        isLoadingFrom10DayPageRef.current = !!hasHourParam;
        return () => {
            if (dataLoadTimeout.current) {
                dataLoadTimeout.current = null;
            }
        };
    }, []);

    const minForecastYValue = useMemo(() => getMinForecast(hourlyForecastPeriods, selectedHourlyForecastChart), [hourlyForecastPeriods, selectedHourlyForecastChart, SlugName]);
    const maxForecastYValue = useMemo(() => getMaxForecast(hourlyForecastPeriods, selectedHourlyForecastChart), [hourlyForecastPeriods, selectedHourlyForecastChart, SlugName]);

    const getForecastChartData = useCallback(() => {
        const getChartData = (
            tempUnit=1,
            chartType: keyof typeof HourlyForecastChartTypes,
            chartMaxYValue: number,
            chartMinYValue: number,
            hourlyForecastData: HourlyForecastPeriodCompact[] = [], 
            locationSolarDateTime: ILocationSolarDateTime[] = []) => {
            const chartData: ChartHourlyForecastPeriodCompact[] = [];
            let chartWidth = 0;
            if (hourlyForecastData?.length) {
                hourlyForecastData.forEach((hourlyForecastPeriod, hourlyForecastPeriodIndex) => {
                    const temperature = parseFloat(hourlyForecastPeriod.t as string);
                    let updatedHourlyData: ChartHourlyForecastPeriodCompact | HourlyForecastPeriodCompact = hourlyForecastPeriod;
                    
                    // Victory charts will not fill area below chart if any or all precip data has value = 0
                    const minPrecipValue = chartMinYValue && chartMaxYValue ? chartMinYValue : 20; // generate some height from 0 at x-axis
                    const { app: adjustedPrecipProbability } = hourlyForecastPeriod;
                    const precipDiff = 100 - adjustedPrecipProbability;
                    const updatePrecipReadingForChart = precipDiff  ?  adjustedPrecipProbability + 20 : adjustedPrecipProbability;
                    const precipProbablility = hourlyForecastPeriod?.app > 0 
                        ? updatePrecipReadingForChart
                        : minPrecipValue;
                    updatedHourlyData =  {
                        ...hourlyForecastPeriod, 
                        app: precipProbablility === 100 ? 93 : precipProbablility, 
                        originalAdjustedPrecipProbability: adjustedPrecipProbability
                    };
                    
                    if (hourlyForecastPeriodIndex === 0) {
                        const initialHourlyData = { ...updatedHourlyData, plottedPointXPosition: 0, originalAdjustedPrecipProbability: precipProbablility, t: temperature };
                        chartData.push(initialHourlyData);
                    }

                    if (isSolarBreak(locationSolarDateTime, hourlyForecastPeriod?.fdtolcl)) {
                        chartWidth += 63;
                    }

                    chartWidth += 174;

                    if (isAdBreak(hourlyForecastPeriodIndex, false)) {
                        chartWidth += 303;
                    }

                    const hourCardPos = document.getElementById(`${hourlyForecastCardBaseId}${hourlyForecastPeriod?.fdtolcl}`);
                    const hourCardsListWrapper = document.getElementById(largeHourlyForecastViewId);
                    const matchPos = hourCardPos?.offsetLeft && hourCardsListWrapper?.offsetLeft ? hourCardPos?.offsetLeft - hourCardsListWrapper?.offsetLeft : 0;
                    const plotPointAtXPosition = matchPos + 69;
                    const newHourCardData = { ...updatedHourlyData, plottedPointXPosition: plotPointAtXPosition, t: temperature };
                    chartData.push(newHourCardData);
                    if (hourlyForecastPeriodIndex === hourlyForecastData?.length - 1) {
                        const lastHourlyData = { ...updatedHourlyData, plottedPointXPosition: chartWidth, t: temperature };
                        chartData.push(lastHourlyData);
                    }
                });
            }

            return {
                chartData,
                chartWidth
            };
        };

        const { chartData, chartWidth } = getChartData(tempUnit, selectedHourlyForecastChart, maxForecastYValue, minForecastYValue, hourlyForecastPeriods, locationSolarDateTime);
        setForecastChartData({
            chartData, 
            chartWidth
        });
    }, [hourlyForecastPeriods, selectedHourlyForecastChart, locationSolarDateTime, SlugName, maxForecastYValue, minForecastYValue, tempUnit]);
    
    const getElementAsync = useCallback(() =>  new Promise(resolve => {
        const getElement = () => {
            // assert last card is in dom
            const lastCard = hourlyForecastPeriods?.[lastHourCardIndex];
            const element = document.getElementById(`${hourlyForecastCardBaseId}${lastCard?.fdtolcl}`);
            if(element) {
                resolve(element);
                getForecastChartData();
            } else {
                requestAnimationFrame(getElement);
            }
        };
        getElement();
    }), [lastHourCardIndex, hourlyForecastPeriods?.[lastHourCardIndex]?.fdtolcl, getForecastChartData],);

    useEffect(() => {
        async function promisifySearchElements() {
            await getElementAsync();
        }
        if (!isLoadingFrom10DayPageRef.current) { // set chart data immediately and generate chart
            promisifySearchElements();
        } else {
            dataLoadTimeout.current = setTimeout(() => {
                promisifySearchElements();
            }, 300);
        }
    }, [getElementAsync]);

    const { chartData, chartWidth } = forecastChartData;

    let yAxisLow = 0;
    let yAxisHigh = 100;
    const xAxisLow = 0, xAxisHigh = chartData?.length && chartWidth ?  chartWidth : 13;

    if (selectedHourlyForecastChart === HourlyForecastChartTypes.temp) {
        yAxisLow = Math.abs(minForecastYValue) >= 0 ? minForecastYValue - 5 : yAxisLow;
        yAxisHigh = Math.abs(maxForecastYValue) >= 0 ? maxForecastYValue + 5 : yAxisHigh;
    }

    return (
        <ForecastChartView
            chartWidth={chartWidth}
            xAxisLow={xAxisLow}
            xAxisHigh={xAxisHigh}
            yAxisLow={yAxisLow}
            yAxisHigh={yAxisHigh}
            chartData={chartData}
            forecastChartName={selectedHourlyForecastChart}
        />
    );
}));